import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  TrophyIcon,
  UserGroupIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { LeagueService, TeamService, SeasonService, ContestantService, PredictionService } from '../../services/firebaseService';
import PredictionHistory from '../../components/PredictionHistory';
import {
  getEliminationTypeDisplayLabel,
  isMultiEliminationWeek,
} from '../../utils/predictionUtils';

export default function LeagueWeekSummary() {
  const { leagueId, seasonId, weekNumber } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [season, setSeason] = useState(null);
  const [week, setWeek] = useState(null);
  const [contestants, setContestants] = useState([]);
  const [teams, setTeams] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [selectedPredictionId, setSelectedPredictionId] = useState(null);

  useEffect(() => {
    loadData();
  }, [leagueId, seasonId, weekNumber]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load league data
      const leagueData = await LeagueService.getLeague(leagueId);
      setLeague(leagueData);
      
      // Load season data
      const seasonData = await SeasonService.getSeason(seasonId);
      setSeason(seasonData);
      
      // Find the specific week
      const weekData = seasonData.weeks?.find(w => w.weekNumber === parseInt(weekNumber));
      if (!weekData) {
        setError('Week not found');
        return;
      }
      setWeek(weekData);
      
      // Load contestants for this season
      const seasonContestants = await ContestantService.getContestantsBySeason(seasonId);
      setContestants(seasonContestants);
      
      // Load teams for this league season
      const teamsData = await TeamService.getTeamsForLeagueSeason(leagueId, seasonId);
      setTeams(teamsData);
      
      // Load predictions for this week from this league
      const predictionsData = await PredictionService.getPredictionsBySeasonAndWeek(seasonId, parseInt(weekNumber));
      const leaguePredictions = predictionsData.filter(p => p.leagueId === leagueId);
      setPredictions(leaguePredictions);
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getContestantName = (contestantId) => {
    if (!contestantId) return 'N/A';
    const contestant = contestants.find(c => c.id === contestantId);
    return contestant ? contestant.name : contestantId;
  };

  const handleViewHistory = (contestantId) => {
    // Find the first prediction for this contestant
    const prediction = predictions.find(p => 
      p.predictions && (
        p.predictions.votedOut === contestantId ||
        p.predictions.fireMakingLoser === contestantId ||
        p.predictions.winner === contestantId ||
        (Array.isArray(p.predictions.votedOut) && p.predictions.votedOut.some(v => v.contestantId === contestantId))
      )
    );
    
    if (prediction) {
      setSelectedPredictionId(prediction.id);
      setShowHistory(true);
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unknown Team';
  };

  const getPredictionStats = () => {
    const stats = {};
    
    // Initialize all contestants with zero counts
    contestants.forEach(contestant => {
      stats[contestant.id] = { 
        name: contestant.name, 
        totalCount: 0,
        teams: [],
        counts: {}
      };
    });
    
    console.log('Debug: Processing predictions for week:', week.eliminationType);
    console.log('Debug: Total predictions:', predictions.length);
    console.log('Debug: Sample prediction:', predictions[0]);
    
    predictions.forEach((prediction, index) => {
      if (!prediction.predictions) {
        console.log(`Debug: Prediction ${index} has no predictions object`);
        return;
      }
      
      const teamName = getTeamName(prediction.teamId);
      console.log(`Debug: Processing prediction ${index} for team ${teamName}:`, prediction.predictions);
      
      // Handle different prediction formats
      if (week.isFinale) {
        // Finale: votedOut, fireMakingLoser, winner
        ['votedOut', 'fireMakingLoser', 'winner'].forEach(key => {
          const contestantId = prediction.predictions[key];
          if (contestantId && stats[contestantId]) {
            if (!stats[contestantId].counts[key]) {
              stats[contestantId].counts[key] = 0;
            }
            stats[contestantId].counts[key]++;
            stats[contestantId].totalCount++;
            stats[contestantId].teams.push({
              teamName,
              teamId: prediction.teamId,
              predictionType: key
            });
          }
        });
      } else if (isMultiEliminationWeek(week)) {
        const maxPos = week.eliminationType === 'triple' ? 3 : 2;
        if (Array.isArray(prediction.predictions.votedOut) && prediction.predictions.votedOut.length > 0 &&
            prediction.predictions.votedOut[0] && typeof prediction.predictions.votedOut[0] === 'object' && 'position' in prediction.predictions.votedOut[0]) {
          prediction.predictions.votedOut.forEach((item) => {
            const contestantId = item?.contestantId || item?.contestantName;
            const pos = item?.position;
            const predictionKey = (pos >= 1 && pos <= maxPos) ? `votedOut${pos}` : null;
            if (contestantId && predictionKey && stats[contestantId]) {
              if (!stats[contestantId].counts[predictionKey]) {
                stats[contestantId].counts[predictionKey] = 0;
              }
              stats[contestantId].counts[predictionKey]++;
              stats[contestantId].totalCount++;
              stats[contestantId].teams.push({
                teamName,
                teamId: prediction.teamId,
                predictionType: predictionKey
              });
            }
          });
        } else {
          const legacyKeys = ['votedOut1', 'votedOut2'];
          if (week.eliminationType === 'triple') legacyKeys.push('votedOut3');
          legacyKeys.forEach(key => {
            const contestantId = prediction.predictions[key];
            if (contestantId && stats[contestantId]) {
              if (!stats[contestantId].counts[key]) {
                stats[contestantId].counts[key] = 0;
              }
              stats[contestantId].counts[key]++;
              stats[contestantId].totalCount++;
              stats[contestantId].teams.push({
                teamName,
                teamId: prediction.teamId,
                predictionType: key
              });
            }
          });
        }
        if (prediction.predictions.votedOut && !Array.isArray(prediction.predictions.votedOut)) {
          // Handle legacy: votedOut as single string in a double week
          const contestantId = prediction.predictions.votedOut;
          console.log(`Debug: Checking votedOut (single): ${contestantId}`);
          if (contestantId && stats[contestantId]) {
            if (!stats[contestantId].counts.votedOut) {
              stats[contestantId].counts.votedOut = 0;
            }
            stats[contestantId].counts.votedOut++;
            stats[contestantId].totalCount++;
            stats[contestantId].teams.push({
              teamName,
              teamId: prediction.teamId,
              predictionType: 'votedOut'
            });
            console.log(`Debug: Added votedOut prediction for ${contestantId} by ${teamName}`);
          } else if (contestantId) {
            console.log(`Debug: Contestant ${contestantId} not found in stats`);
          }
        }
      } else {
        // Single elimination: votedOut
        const contestantId = prediction.predictions.votedOut;
        if (contestantId && stats[contestantId]) {
          if (!stats[contestantId].counts.votedOut) {
            stats[contestantId].counts.votedOut = 0;
          }
          stats[contestantId].counts.votedOut++;
          stats[contestantId].totalCount++;
          stats[contestantId].teams.push({
            teamName,
            teamId: prediction.teamId,
            predictionType: 'votedOut'
          });
        }
      }
    });
    
    console.log('Debug: Final stats:', stats);
    return stats;
  };

  const getNonPredictingTeams = () => {
    const leagueTeamIds = new Set(teams.map(t => t.id));
    const teamIdToMadePrediction = new Map();
    teams.forEach(team => {
      teamIdToMadePrediction.set(team.id, false);
    });

    predictions.forEach(prediction => {
      if (!leagueTeamIds.has(prediction.teamId)) return;
      const predObj = prediction.predictions || {};
      
      let made = false;
      if (week.isFinale) {
        // Finale: check votedOut, fireMakingLoser, winner
        made = ['votedOut', 'fireMakingLoser', 'winner'].some(key => 
          predObj[key] && predObj[key] !== 'n/a'
        );
      } else if (isMultiEliminationWeek(week)) {
        const legacyKeys = ['votedOut1', 'votedOut2'];
        if (week.eliminationType === 'triple') legacyKeys.push('votedOut3');
        made = legacyKeys.some(key =>
          predObj[key] && predObj[key] !== 'n/a'
        );
        
        // Also check if votedOut is an array with valid predictions
        if (!made && Array.isArray(predObj.votedOut)) {
          made = predObj.votedOut.some(item => {
            const contestantId = item.contestantId || item.contestantName || item;
            return contestantId && contestantId !== 'n/a';
          });
        }
        
        // Also check single elimination format
        if (!made && predObj.votedOut && !Array.isArray(predObj.votedOut)) {
          made = predObj.votedOut && predObj.votedOut !== 'n/a';
        }
      } else {
        // Single elimination: check votedOut
        made = predObj.votedOut && predObj.votedOut !== 'n/a';
      }
      
      if (made) {
        teamIdToMadePrediction.set(prediction.teamId, true);
      }
    });

    const nonPredictors = [];
    teamIdToMadePrediction.forEach((made, teamId) => {
      if (!made) {
        nonPredictors.push(getTeamName(teamId));
      }
    });

    nonPredictors.sort((a, b) => a.localeCompare(b));
    return nonPredictors;
  };

  const getCorrectPredictions = () => {
    if (!week.results || Object.keys(week.results).length === 0) return [];
    
    const correct = [];
    
    if (week.isFinale) {
      // Check finale predictions
      predictions.forEach(prediction => {
        const correctPredictions = [];
        if (prediction.predictions.votedOut === week.results.votedOut) {
          correctPredictions.push('votedOut');
        }
        if (prediction.predictions.fireMakingLoser === week.results.fireMakingLoser) {
          correctPredictions.push('fireMakingLoser');
        }
        if (prediction.predictions.winner === week.results.winner) {
          correctPredictions.push('winner');
        }
        
        if (correctPredictions.length > 0) {
          correct.push({
            teamId: prediction.teamId,
            teamName: getTeamName(prediction.teamId),
            correctPredictions
          });
        }
      });
    } else if (isMultiEliminationWeek(week)) {
      const votedOut = week.results.votedOut;
      if (Array.isArray(votedOut)) {
        predictions.forEach(prediction => {
          const correctPredictions = [];
          [...votedOut]
            .sort((a, b) => (a.position || 0) - (b.position || 0))
            .forEach((result) => {
              const position = result.position;
              const predictionKey = `votedOut${position}`;
              const resultContestantId = result.contestantId || result.contestantName;
              if (prediction.predictions[predictionKey] === resultContestantId) {
                correctPredictions.push(predictionKey);
              }
            });
          
          if (correctPredictions.length > 0) {
            correct.push({
              teamId: prediction.teamId,
              teamName: getTeamName(prediction.teamId),
              correctPredictions
            });
          }
        });
      }
    } else {
      // Check single elimination predictions
      predictions.forEach(prediction => {
        if (prediction.predictions.votedOut === week.results.votedOut) {
          correct.push({
            teamId: prediction.teamId,
            teamName: getTeamName(prediction.teamId),
            correctPredictions: ['votedOut']
          });
        }
      });
    }
    
    return correct;
  };

  const getDraftedContestants = () => {
    const drafted = [];
    
    teams.forEach(team => {
      if (team.draftPicks && Array.isArray(team.draftPicks)) {
        team.draftPicks.forEach(draftPick => {
          // Handle both old format (contestantId as string) and new format (object with contestant property)
          const contestantName = draftPick.contestantId || draftPick.contestant?.contestantId || draftPick;
          const contestantId = draftPick.contestantId || draftPick.contestant?.contestantId || draftPick;
          
          drafted.push({
            teamId: team.id,
            teamName: team.name,
            contestantId,
            contestantName
          });
        });
      }
    });
    
    return drafted;
  };

  const getEliminatedContestants = () => {
    if (!week.results || Object.keys(week.results).length === 0) return [];
    
    const eliminated = [];
    
    if (week.isFinale) {
      eliminated.push(week.results.votedOut);
      eliminated.push(week.results.fireMakingLoser);
      // Winner is not eliminated, but we might want to show them differently
    } else if (isMultiEliminationWeek(week)) {
      const votedOut = week.results.votedOut;
      if (Array.isArray(votedOut)) {
        votedOut.forEach(result => {
          const contestantId = result.contestantId || result.contestantName;
          eliminated.push(contestantId);
        });
      }
    } else {
      eliminated.push(week.results.votedOut);
    }
    
    return eliminated;
  };

  const getTeamsWithDraftedEliminated = () => {
    const drafted = getDraftedContestants();
    const eliminated = getEliminatedContestants();
    
    // Convert eliminated contestant IDs to names for comparison
    const eliminatedNames = eliminated.map(contestantId => getContestantName(contestantId));
    
    return drafted.filter(d => eliminatedNames.includes(d.contestantName));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/manage-leagues/${leagueId}`)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to League
          </button>
        </div>
      </div>
    );
  }

  const predictionStats = getPredictionStats();
  const correctPredictions = getCorrectPredictions();
  const teamsWithDraftedEliminated = getTeamsWithDraftedEliminated();
  const eliminatedContestants = getEliminatedContestants();
  const nonPredictingTeams = getNonPredictingTeams();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/manage-leagues/${leagueId}`)}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Week {weekNumber} Summary
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  {league?.name || 'Loading...'} • {season?.name || 'Loading...'} • {teams?.length || 0} teams
                </p>
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {week?.isFinale
                      ? 'Finale'
                      : getEliminationTypeDisplayLabel(week) || 'Single Elimination'}
                  </span>
                  {week?.isMergeWeek && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Merge Week
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(`/manage-leagues/${leagueId}/season/${seasonId}/week/${weekNumber}/email-recap`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email Recap
              </button>
            </div>
          </div>
        </div>

        {/* Results Section */}
        {week.results && Object.keys(week.results).length > 0 && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <TrophyIcon className="h-6 w-6 text-gray-400 mr-2" />
                  <h2 className="text-lg font-medium text-gray-900">Results</h2>
                </div>
              </div>
              <div className="px-6 py-4">
                {week.isFinale ? (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 w-32">Winner:</span>
                        <span className="text-sm text-gray-900">{getContestantName(week.results.winner)}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 w-32">Fire-making loser:</span>
                        <span className="text-sm text-gray-900">{getContestantName(week.results.fireMakingLoser)}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 w-32">Voted out:</span>
                        <div className="flex-1">
                          <span className="text-sm text-gray-900">{getContestantName(week.results.votedOut)}</span>
                          {teamsWithDraftedEliminated.length > 0 && (
                            <div className="mt-1">
                              <span className="text-xs text-gray-500">Drafted by: </span>
                              <span className="text-xs text-gray-600">
                                {teamsWithDraftedEliminated.map((team, index) => team.teamName).join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : isMultiEliminationWeek(week) && Array.isArray(week.results.votedOut) ? (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <span className="text-sm font-medium text-gray-700">Voted out:</span>
                      {week.results.votedOut
                        .sort((a, b) => a.position - b.position)
                        .map((result, index) => {
                          // Handle both old format (contestantId) and new format (contestantName)
                          const contestantId = result.contestantId || result.contestantName;
                          const contestantName = result.contestantName || getContestantName(result.contestantId);
                          
                          // Find teams that drafted this specific contestant
                          const teamsThatDraftedThisContestant = teamsWithDraftedEliminated.filter(team => 
                            team.contestantName === contestantName || team.contestantId === contestantId
                          );
                          
                          return (
                            <div key={`${result.position}-${contestantId}`} className="ml-4">
                              <div className="flex items-center">
                                <span className="text-sm text-gray-600">#{result.position}:</span>
                                <span className="text-sm text-gray-900 ml-2">{contestantName}</span>
                              </div>
                              {teamsThatDraftedThisContestant.length > 0 && (
                                <div className="ml-6 mt-1">
                                  <span className="text-xs text-gray-500">Drafted by: </span>
                                  <span className="text-xs text-gray-600">
                                    {teamsThatDraftedThisContestant.map(team => team.teamName).join(', ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 w-32">Voted out:</span>
                      <div className="flex-1">
                        <span className="text-sm text-gray-900">{getContestantName(week.results.votedOut)}</span>
                        {teamsWithDraftedEliminated.length > 0 && (
                          <div className="mt-1">
                            <span className="text-xs text-gray-500">Drafted by: </span>
                            <span className="text-xs text-gray-600">
                              {teamsWithDraftedEliminated.map((team, index) => team.teamName).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Prediction Statistics */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <UserGroupIcon className="h-6 w-6 text-gray-400 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">Prediction Breakdown</h2>
              </div>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                {Object.entries(predictionStats)
                  .filter(([, data]) => data.totalCount > 0)
                  .sort(([,a], [,b]) => b.totalCount - a.totalCount)
                  .map(([contestantId, data]) => {
                    const isCorrect = eliminatedContestants.includes(contestantId);
                    return (
                      <div key={contestantId} className={`border rounded-lg p-4 ${isCorrect ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <h3 className="text-lg font-medium text-gray-900">{data.name}</h3>
                            {isCorrect && (
                              <CheckIcon className="ml-2 h-5 w-5 text-green-500" />
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                              isCorrect 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {data.totalCount} prediction{data.totalCount !== 1 ? 's' : ''}
                            </span>
                            <button
                              onClick={() => handleViewHistory(contestantId)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                              title="View prediction history"
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              History
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {data.teams.map((team, index) => (
                            <div key={index} className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">{team.teamName}</span>
                              <span className="text-xs text-gray-500 capitalize">
                                {team.predictionType.replace(/([A-Z])/g, ' $1').trim()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                {nonPredictingTeams.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
                    <span className="font-medium">Didn't make a prediction</span> ({nonPredictingTeams.length}): {nonPredictingTeams.join(', ')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>


      </div>
      
      {/* Prediction History Modal */}
      {showHistory && selectedPredictionId && (
        <PredictionHistory 
          predictionId={selectedPredictionId}
          onClose={() => {
            setShowHistory(false);
            setSelectedPredictionId(null);
          }}
        />
      )}
    </div>
  );
}
